/**
 * useBilling Hook
 * React Query hooks for billing operations
 * Supports both post-paid (enterprise) and token-based billing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import billingService, { BILLING_TYPES, OURBUS_PRICING } from '../services/billingService';
import { useTenant } from '../context/TenantContext';

// Query keys
export const BILLING_KEYS = {
  subscription: ['billing', 'subscription'],
  summary: ['billing', 'summary'],
  currentCharges: ['billing', 'current-charges'],
  usage: (period) => ['billing', 'usage', period],
  invoices: (params) => ['billing', 'invoices', params],
};

/**
 * Hook to get current subscription with billing type
 */
export function useSubscription(options = {}) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: [...BILLING_KEYS.subscription, currentTenant?.id],
    queryFn: billingService.getSubscription,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to get billing summary for current period
 */
export function useBillingSummary(month = null, options = {}) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: [...BILLING_KEYS.summary, currentTenant?.id, month],
    // Wrap to avoid passing React Query context as month parameter
    queryFn: () => billingService.getBillingSummary(month),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    ...options,
  });
}

/**
 * Hook to get real-time current charges
 * Auto-refreshes for live billing display
 */
export function useCurrentCharges(options = {}) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: [...BILLING_KEYS.currentCharges, currentTenant?.id],
    queryFn: billingService.getCurrentCharges,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: options.refetchInterval || 5 * 60 * 1000, // Default 5 minutes
    ...options,
  });
}

/**
 * Hook to determine billing type for current tenant
 */
export function useBillingType() {
  const { currentTenant } = useTenant();

  return useMemo(() => {
    const tenantId = currentTenant?.id || currentTenant?.slug;
    const billingType = billingService.getBillingType(tenantId);
    const isPostPaid = billingType === BILLING_TYPES.POST_PAID;
    const isTokenBased = billingType === BILLING_TYPES.TOKEN_BASED;

    return {
      billingType,
      isPostPaid,
      isTokenBased,
      isEnterprise: isPostPaid,
      pricing: isPostPaid ? OURBUS_PRICING : null,
    };
  }, [currentTenant?.id, currentTenant?.slug]);
}

/**
 * Hook to update subscription
 */
export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: billingService.updateSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.subscription });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.summary });
    },
  });
}

/**
 * Hook to get usage data
 */
export function useUsage(period = 'month', options = {}) {
  return useQuery({
    queryKey: BILLING_KEYS.usage(period),
    queryFn: () => billingService.getUsage({ period }),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to get invoices
 */
export function useInvoices(params = {}, options = {}) {
  return useQuery({
    queryKey: BILLING_KEYS.invoices(params),
    queryFn: () => billingService.getInvoices(params),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to create top-up order (legacy - token-based only)
 */
export function useCreateTopUp() {
  return useMutation({
    mutationFn: billingService.createTopUp,
  });
}

/**
 * Hook to create payment order via payment gateway
 * Supports Razorpay and Eazybuzz
 */
export function useCreatePaymentOrder() {
  return useMutation({
    mutationFn: billingService.createPaymentOrder,
  });
}

/**
 * Hook to verify payment after completion
 */
export function useVerifyPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: billingService.verifyPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.subscription });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.summary });
      queryClient.invalidateQueries({ queryKey: ['billing', 'usage'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'token-balance'] });
    },
  });
}

/**
 * Hook to calculate token price (client-side)
 */
export function useTokenPrice(tokens) {
  return useMemo(() => {
    if (!tokens || tokens <= 0) return null;
    return billingService.calculatePrice(tokens);
  }, [tokens]);
}

/**
 * Hook to calculate enterprise bill (post-paid)
 */
export function useEnterpriseBill(trips, grossRevenue) {
  return useMemo(() => {
    if (!trips || !grossRevenue) return null;
    return billingService.calculateEnterpriseBill(trips, grossRevenue);
  }, [trips, grossRevenue]);
}

/**
 * Hook to get token balance
 */
export function useTokenBalance(options = {}) {
  const { isTokenBased } = useBillingType();

  return useQuery({
    queryKey: ['billing', 'token-balance'],
    queryFn: billingService.getTokenBalance,
    enabled: isTokenBased,
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
}

// =========================================================================
// Admin Hooks
// =========================================================================

/**
 * Hook to get all billing plans (admin only)
 */
export function useAdminBillingPlans(options = {}) {
  return useQuery({
    queryKey: ['admin', 'billing', 'plans'],
    queryFn: billingService.adminGetAllPlans,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to get a specific billing plan (admin only)
 */
export function useAdminBillingPlan(clientId, options = {}) {
  return useQuery({
    queryKey: ['admin', 'billing', 'plan', clientId],
    queryFn: () => billingService.adminGetPlan(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to save/update billing plan (admin only)
 */
export function useAdminSaveBillingPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, planData }) => billingService.adminSavePlan(clientId, planData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'billing', 'plans'] });
    },
  });
}

export default {
  useSubscription,
  useBillingSummary,
  useBillingType,
  useUpdateSubscription,
  useUsage,
  useInvoices,
  useCreateTopUp,
  useCreatePaymentOrder,
  useVerifyPayment,
  useTokenPrice,
  useEnterpriseBill,
  useTokenBalance,
  useAdminBillingPlans,
  useAdminBillingPlan,
  useAdminSaveBillingPlan,
};

